import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (V-D admin endpoints — legacy /admin/* envelope):
//
//   POST /admin/coach_list   { page, limit, search?, sortyBy?, sortDirection? }
//                           → { success, data: { docs: AdminCoach[], totalDocs, ... }, message }
//
//   POST /admin/coach_get    { id }
//                           → { success, data: AdminCoach, message }
//
//   POST /admin/coach_create { name, bio, topics, avatar, cover, display_order, is_published }
//   POST /admin/coach_update { id, ...same fields }
//   POST /admin/coach_delete { id }
//
// `avatar` / `cover` are sent as the same S3-response object that
// /admin/album_create's `S3_cover_image` accepts ({ Location, Key }).
// Backend pickS3 also tolerates lowercase variants. On edit, omit the
// field to keep the existing image.
// ───────────────────────────────────────────────────────────────────────

export type AdminCoach = {
  _id: string
  name: string
  slug?: string
  avatar_url?: string
  avatar_key?: string
  cover_url?: string
  bio?: string
  /** Short one-line headline shown on the CoachesFollow onboarding rail. */
  tagline?: string
  topics?: string[]
  source?: "backfilled" | "admin"
  is_published?: boolean
  /** V-Q5: directory-listing gate. Distinct from is_published — controls
   *  whether the coach appears in /v2/coaches + /featured. Backfilled
   *  author stubs have is_published:true / isPublicCoach:false so they
   *  resolve on content cards but stay out of the browse-coaches list. */
  isPublicCoach?: boolean
  /** V-L Phase 2: admin-set "✓ verified" checkmark shown on mobile. */
  verified?: boolean
  /** P1 paid DMs: admin-set price tier in cents (null = disabled). */
  message_rate_cents?: number | null
  /** P1 paid DMs: creator-side opt-in toggle (read-only from admin). */
  messaging_enabled?: boolean
  /** V-O: admin toggle. When false, mobile hides the "Ask" CTA on
   *  this coach's cards. Default true. */
  acceptsConversations?: boolean
  /** V-Q12: admin toggle. When false, mobile hides the "Subscribe"
   *  CTA on this coach's cards (independent of the app-wide feature
   *  flag). Default true. */
  subscriptionEnabled?: boolean
  /** V-L Creator Profile redesign — public contact + social fields. */
  email?: string
  website?: string
  socials?: {
    instagram?: string
    twitter?: string
    linkedin?: string
    tiktok?: string
    facebook?: string
  }
  display_order?: number
  created_at?: string
  updated_at?: string
}

type CoachListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
  /** V-Q11: restrict to published (true) or unpublished (false)
   *  coaches. Omitted → both. Coaches page passes `true` so the
   *  ~33 backfilled author stubs (JIM ROHN, TONY ROBBINS, …) stay
   *  out of the ops directory. */
  is_published?: boolean
}

export type CoachListResponse = {
  docs: AdminCoach[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

export function useCoachesList(params: CoachListParams) {
  return useQuery({
    queryKey: ["admin", "coaches", "list", params] as const,
    queryFn: async (): Promise<CoachListResponse> => {
      const res = await api.post<{
        success: boolean
        data: CoachListResponse
        message: string
      }>("/admin/coach_list", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

/**
 * Fetch all coaches as `{id, name}[]` sorted alphabetically.
 *
 * Used by the Messages compose form (V-I) to populate the coach picker.
 * Hits /admin/coach_list with a large `limit` so the dropdown contains
 * every coach in one request — the dataset is small (dozens, not
 * thousands) so pagination isn't worth the UX complexity.
 */
export type CoachOption = {
  id: string
  name: string
  avatar_url?: string
}

export function useAllCoaches() {
  return useQuery({
    queryKey: ["admin", "coaches", "all", "published"] as const,
    queryFn: async (): Promise<CoachOption[]> => {
      const res = await api.post<{
        success: boolean
        data: CoachListResponse
        message: string
      }>("/admin/coach_list", {
        page: 1,
        limit: 500,
        // V-Q11 (Kush 2026-08-25): MessageCompose Creator dropdown
        // must only surface REAL coaches — the ~33 backfilled author
        // stubs (is_published:false) exist purely to resolve
        // narrator_coach_id FKs on legacy chapter/album/audioQuote
        // docs and shouldn't be selectable when sending a message.
        is_published: true,
      })
      const docs = Array.isArray(res.data?.data?.docs)
        ? res.data.data.docs
        : []
      return docs
        .map((c) => ({
          id: c._id,
          name: c.name || "(unnamed)",
          avatar_url: c.avatar_url,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    staleTime: 60_000,
  })
}

export function useGetCoach(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "coaches", "get", id] as const,
    queryFn: async (): Promise<AdminCoach | null> => {
      if (!id) return null
      const res = await api.post<{
        success: boolean
        data: AdminCoach
        message: string
      }>("/admin/coach_get", { id })
      return res.data?.data ?? null
    },
    enabled: !!id,
  })
}

export type CoachWritePayload = {
  /** Present for update; omitted for create. */
  id?: string
  name: string
  bio?: string
  /** One-line headline for the CoachesFollow rail (≤ 120 chars). */
  tagline?: string
  topics?: string[]
  /** Either the s3response object (new upload) or omitted (keep existing). */
  avatar?: unknown
  cover?: unknown
  display_order?: number
  is_published?: boolean
  /** V-Q5: directory-listing toggle (independent of is_published). */
  isPublicCoach?: boolean
  /** V-L Phase 2 admin toggles. */
  verified?: boolean
  /** V-O: admin toggle to hide the "Ask" CTA on mobile coach cards. */
  acceptsConversations?: boolean
  /** V-Q12: admin toggle to hide the "Subscribe" CTA on mobile coach cards. */
  subscriptionEnabled?: boolean
  /** P1 paid DMs admin tier (cents) — null clears. */
  message_rate_cents?: number | null
  /** V-L Creator Profile: public contact + social fields. Empty string clears. */
  email?: string
  website?: string
  socials?: {
    instagram?: string
    twitter?: string
    linkedin?: string
    tiktok?: string
    facebook?: string
  }
}

export function useCreateCoach() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CoachWritePayload) => {
      const res = await api.post<{
        success: boolean
        data?: AdminCoach
        message: string
      }>("/admin/coach_create", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "coaches"] })
    },
  })
}

export function useUpdateCoach() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CoachWritePayload) => {
      const res = await api.post<{
        success: boolean
        data?: AdminCoach
        message: string
      }>("/admin/coach_update", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "coaches"] })
    },
  })
}

/**
 * One-shot bulk-publish for the 700+ records the seeder created with
 * `source: "backfilled", is_published: false`. Drives the "Publish all
 * backfilled" button on the Coaches list.
 */
export function useBulkPublishBackfilled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        success: boolean
        data: { matched: number; modified: number }
        message: string
      }>("/admin/coach_bulk_publish_backfilled", {})
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "coaches"] })
    },
  })
}

export function useDeleteCoach() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/coach_delete", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "coaches"] })
    },
  })
}
